import { useState } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

interface FAQItem {
  question: string;
  answer: string;
  category: 'general' | 'process' | 'costs' | 'credit';
}

const faqData: FAQItem[] = [
  {
    question: "How much can I save with debt settlement?",
    answer: "Our clients typically save 40-60% of their original debt amount. The exact savings depend on your specific financial situation, types of debt, and creditor negotiations. Some clients have saved as much as 70% of their total debt.",
    category: 'general'
  },
  {
    question: "How long does debt settlement take?",
    answer: "Most debt settlement programs take 24-48 months to complete, depending on the amount of debt and your ability to make monthly payments into the settlement fund. The timeline can vary based on creditor cooperation and your financial circumstances.",
    category: 'process'
  },
  {
    question: "Will debt settlement affect my credit score?",
    answer: "Debt settlement may initially impact your credit score, but most clients see improvement within 12-24 months after completing the program, as they eliminate debt and rebuild their credit. The long-term benefit of being debt-free often outweighs the temporary credit impact.",
    category: 'credit'
  },
  {
    question: "What types of debt can be settled?",
    answer: "We can help settle unsecured debts including credit cards, personal loans, medical bills, department store cards, and some business debts. Secured debts like mortgages and car loans typically cannot be settled through our program.",
    category: 'general'
  },
  {
    question: "How much does debt settlement cost?",
    answer: "Our fees are performance-based, meaning you only pay when we successfully settle your debts. Typically, fees range from 20-25% of the enrolled debt amount, and you only pay as each debt is settled - never upfront.",
    category: 'costs'
  },
  {
    question: "Do I need to stop paying my creditors?",
    answer: "Yes, to create the financial hardship necessary for creditors to accept settlement offers, you'll need to stop making minimum payments on enrolled debts. Instead, you'll make deposits into a dedicated settlement fund that we'll use to pay settlements.",
    category: 'process'
  },
  {
    question: "Can creditors still call me during the program?",
    answer: "Initially, creditors may continue calling, but we provide guidance on how to handle these calls. Many clients see a significant reduction in creditor calls after a few months as we begin negotiating on their behalf.",
    category: 'process'
  },
  {
    question: "Is debt settlement better than bankruptcy?",
    answer: "Debt settlement can be an alternative to bankruptcy for many people. Unlike bankruptcy, debt settlement allows you to avoid court proceedings, keep your assets, and potentially preserve more of your credit rating long-term. However, the best option depends on your specific situation.",
    category: 'general'
  },
  {
    question: "What happens if a creditor won't settle?",
    answer: "While most creditors are willing to settle, if one refuses, we'll work with you to explore other options. This might include continuing negotiations, payment plans, or removing that debt from the program. Our success rate is over 90%.",
    category: 'process'
  },
  {
    question: "Can I settle business debts?",
    answer: "Yes, we offer specialized business debt relief services for small to medium-sized businesses. This includes settling merchant cash advances, business credit cards, equipment financing, and vendor debts. Business debt settlement follows a similar process but with different negotiation strategies.",
    category: 'general'
  },
  {
    question: "How much money do I need to save monthly?",
    answer: "Most clients need to save 15-20% of their enrolled debt amount over the course of the program. For example, if you enroll $50,000 in debt, you'd typically need to save $400-600 per month. We'll work with you to determine an affordable payment plan.",
    category: 'costs'
  },
  {
    question: "Are there any tax implications?",
    answer: "Forgiven debt over $600 may be considered taxable income by the IRS. However, if you're insolvent (debts exceed assets), you may qualify for tax exclusion. We recommend consulting with a tax professional about your specific situation.",
    category: 'costs'
  }
];

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'fas fa-list' },
    { id: 'general', name: 'General', icon: 'fas fa-info-circle' },
    { id: 'process', name: 'Process', icon: 'fas fa-cogs' },
    { id: 'costs', name: 'Costs & Fees', icon: 'fas fa-dollar-sign' },
    { id: 'credit', name: 'Credit Impact', icon: 'fas fa-credit-card' }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqData 
    : faqData.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Set document metadata using React 19 features
  useEffect(() => {
    document.title = 'Frequently Asked Questions - Debt Settlement | Brite Solutions';
    
    // Update meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get answers to common questions about debt settlement, credit impact, costs, and our process. Learn how Brite Solutions can help you get out of debt with up to 60% savings.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'debt settlement FAQ, debt relief questions, credit impact, settlement costs, debt help');
    }
    
    // Add FAQ Schema
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-teal-100 mb-8">
            Everything you need to know about debt settlement and our services
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="tel:+18003328373"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white font-semibold rounded-md hover:bg-white hover:text-teal-600 transition-colors whitespace-nowrap"
            >
              <i className="fas fa-phone mr-2"></i>
              Call for Free Consultation
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filter */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium transition-colors whitespace-nowrap ${
                    activeCategory === category.id
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  <i className={`${category.icon} text-sm`}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 text-lg">{faq.question}</h3>
                  <i className={`fas fa-chevron-${openFAQ === index ? 'up' : 'down'} text-gray-400`}></i>
                </button>
                
                {openFAQ === index && (
                  <div className="px-6 pb-4 border-t border-gray-100">
                    <p className="text-gray-700 leading-relaxed pt-4">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-16 text-center bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              Our debt settlement specialists are here to help you understand your options
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <a
                href="tel:+18003328373"
                className="inline-flex items-center justify-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="fas fa-phone mr-2"></i>
                Call (800) DEBT-FREE
              </a>
              
              <button
                onClick={() => {
                  const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (widget) widget.click();
                }}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-800 text-white font-semibold rounded-md hover:bg-gray-900 transition-colors whitespace-nowrap"
              >
                <i className="fas fa-comments mr-2"></i>
                Free Chat Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
